import { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { X, Sparkles, Copy, Check } from 'lucide-react'
import { Button } from '../ui/Button'
import { useGenerateSpec } from '../../api/meetings'
import { useAiStatus } from '../../api/settings'
import { cn } from '../../lib/utils'

interface GenerateSpecModalProps {
  meetingId: string
  isOpen: boolean
  onClose: () => void
  onSave?: (specification: string) => void
}

export function GenerateSpecModal({ meetingId, isOpen, onClose, onSave }: GenerateSpecModalProps) {
  const [selectedModel, setSelectedModel] = useState<string>('')
  const [additionalContext, setAdditionalContext] = useState('')
  const [result, setResult] = useState<{
    specification: string
    tokensUsed: { input: number; output: number }
    cost: number
    model: string
  } | null>(null)
  const [copied, setCopied] = useState(false)

  const { data: aiStatus } = useAiStatus()
  const generateSpec = useGenerateSpec()

  const handleGenerate = async () => {
    try {
      const data = await generateSpec.mutateAsync({
        meetingId,
        model: selectedModel || undefined,
        additionalContext: additionalContext || undefined,
      })
      setResult(data)
    } catch {
      // Error handled by mutation state
    }
  }

  const handleCopy = async () => {
    if (result?.specification) {
      await navigator.clipboard.writeText(result.specification)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleSave = () => {
    if (result?.specification && onSave) {
      onSave(result.specification)
      onClose()
    }
  }

  const handleClose = () => {
    setResult(null)
    setAdditionalContext('')
    onClose()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className="relative bg-bg-card border border-border-medium backdrop-blur-glass w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Генерация ТЗ</h2>
                  <p className="text-xs text-text-tertiary">AI создаст ТЗ на основе транскрипта</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-text-secondary hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {!result ? (
                <>
                  {/* AI Status */}
                  {aiStatus && !aiStatus.configured && (
                    <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-400 text-sm">
                      AI не настроен. Добавьте ANTHROPIC_API_KEY в настройках сервера.
                    </div>
                  )}

                  {/* Model Selection */}
                  <div>
                    <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                      Модель AI
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {(aiStatus?.availableModels || []).map((model) => (
                        <button
                          key={model.id}
                          onClick={() => setSelectedModel(model.id)}
                          className={cn(
                            'p-3 rounded-lg border text-left transition-all',
                            (selectedModel || aiStatus?.model) === model.id
                              ? 'border-violet-500/40 bg-violet-500/10'
                              : 'border-white/10 bg-white/5 hover:bg-white/10'
                          )}
                        >
                          <div className="text-sm font-medium text-white">{model.name}</div>
                          <div className="text-xs text-text-tertiary mt-0.5">{model.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Additional Context */}
                  <div>
                    <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                      Дополнительный контекст (опционально)
                    </label>
                    <textarea
                      value={additionalContext}
                      onChange={(e) => setAdditionalContext(e.target.value)}
                      placeholder="Укажите дополнительные требования, ограничения или контекст..."
                      className="w-full h-24 px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-violet-500/50 resize-none text-sm"
                    />
                  </div>

                  {/* Generate Button */}
                  <Button
                    onClick={handleGenerate}
                    disabled={generateSpec.isPending || (aiStatus && !aiStatus.configured)}
                    loading={generateSpec.isPending}
                    className="w-full bg-violet-600 hover:bg-violet-700"
                    size="lg"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {generateSpec.isPending ? 'Генерация...' : 'Сгенерировать ТЗ'}
                  </Button>

                  {generateSpec.isError && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                      {generateSpec.error instanceof Error ? generateSpec.error.message : 'Ошибка генерации'}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Result */}
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-text-secondary">
                      Модель: <span className="text-text-primary">{result.model}</span>
                      {' · '}
                      Токены: <span className="text-text-primary">{result.tokensUsed.input + result.tokensUsed.output}</span>
                      {' · '}
                      Стоимость: <span className="text-text-primary">${result.cost.toFixed(4)}</span>
                    </div>
                    <button
                      onClick={handleCopy}
                      className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-white transition-colors"
                    >
                      {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                      {copied ? 'Скопировано' : 'Копировать'}
                    </button>
                  </div>

                  {/* Markdown preview */}
                  <div className="bg-white/5 border border-white/10 rounded-lg p-5 max-h-[50vh] overflow-y-auto">
                    <div className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap text-text-primary text-sm leading-relaxed">
                      {result.specification}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={() => setResult(null)}
                      variant="secondary"
                      className="flex-1"
                    >
                      Перегенерировать
                    </Button>
                    {onSave && (
                      <Button
                        onClick={handleSave}
                        className="flex-1 bg-accent-lime/15 text-accent-lime hover:bg-accent-lime/25"
                      >
                        Сохранить в заявку
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
